import {
  ArrowDownCircle,
  ArrowUpCircle,
  DoorClosed,
  DoorOpen,
  FileText,
  History,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../shared/components/Modal';
import { formatBRL, formatDateTime } from '../../shared/lib/format';
import { caixaApi } from './api';
import { RelatorioZModal } from './components/RelatorioZModal';
import {
  useAbrirCaixa,
  useCaixaAtual,
  useCaixaResumo,
  useFecharCaixa,
  useMovimentoCaixa,
} from './hooks';
import type { Caixa, MovimentoTipo, ResumoCaixa } from './types';

const FORMA_LABEL: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Debito',
  cartao_credito: 'Credito',
  vale_refeicao: 'Vale Refeicao',
  outro: 'Outro',
};

export function CaixaPage() {
  const { data: caixa, isLoading } = useCaixaAtual();
  const { data: resumo } = useCaixaResumo(caixa?.id);

  const abrirMut = useAbrirCaixa();
  const fecharMut = useFecharCaixa();
  const movMut = useMovimentoCaixa();

  const [abrirOpen, setAbrirOpen] = useState(false);
  const [fecharOpen, setFecharOpen] = useState(false);
  const [movOpen, setMovOpen] = useState<MovimentoTipo | null>(null);
  const [relatorioResumo, setRelatorioResumo] = useState<ResumoCaixa | null>(null);
  const [historicoOpen, setHistoricoOpen] = useState(false);

  const totalPorForma = resumo?.por_forma ?? {};
  const totalVendas = Object.values(totalPorForma).reduce((a, b) => a + b, 0);
  const totalDinheiro = Number(totalPorForma.dinheiro ?? 0);
  const totalReforco = (resumo?.movimentos ?? [])
    .filter((m) => m.tipo === 'reforco')
    .reduce((a, m) => a + Number(m.valor), 0);
  const totalSangria = (resumo?.movimentos ?? [])
    .filter((m) => m.tipo === 'sangria')
    .reduce((a, m) => a + Number(m.valor), 0);
  const esperadoGaveta =
    Number(caixa?.valor_abertura ?? 0) + totalDinheiro + totalReforco - totalSangria;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Caixa</h1>
        <p className="mt-1 text-sm text-slate-500">
          Controle o turno do operador: abertura, sangrias, reforcos e fechamento com
          conferencia de valores.
        </p>
      </header>

      {isLoading ? (
        <div className="card h-40 animate-pulse" />
      ) : !caixa ? (
        <div className="card p-6 text-center">
          <Wallet className="mx-auto mb-3 h-10 w-10 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-900">Nenhum caixa aberto</h2>
          <p className="mt-1 text-sm text-slate-500">
            Abra o caixa para comecar a registrar vendas no PDV.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button className="btn-primary" onClick={() => setAbrirOpen(true)}>
              <DoorOpen className="h-4 w-4" />
              Abrir caixa
            </button>
            <button className="btn-secondary" onClick={() => setHistoricoOpen(true)}>
              <History className="h-4 w-4" />
              Historico
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Status" value="Aberto" accent="emerald" />
            <Stat
              label="Aberto em"
              value={formatDateTime(caixa.abertura_em)}
              small
            />
            <Stat label="Valor inicial" value={formatBRL(Number(caixa.valor_abertura))} />
            <Stat
              label="Vendas no caixa"
              value={String(resumo?.qtd_vendas ?? 0)}
              accent="brand"
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <div className="card p-5 lg:col-span-2">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">
                Totais por forma de pagamento
              </h3>
              {Object.keys(totalPorForma).length === 0 ? (
                <p className="text-sm text-slate-500">Nenhuma venda registrada ainda.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {Object.entries(totalPorForma).map(([forma, valor]) => (
                    <div
                      key={forma}
                      className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
                    >
                      <span className="text-sm text-slate-600">
                        {FORMA_LABEL[forma] ?? forma}
                      </span>
                      <span className="font-semibold text-slate-900">
                        {formatBRL(Number(valor))}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-lg bg-brand-50 px-3 py-2 sm:col-span-2">
                    <span className="text-sm font-medium text-brand-900">Total de vendas</span>
                    <span className="font-bold text-brand-700">{formatBRL(totalVendas)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="card p-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Gaveta esperada</h3>
              <dl className="space-y-1 text-sm">
                <Row label="Abertura" value={formatBRL(Number(caixa.valor_abertura))} />
                <Row label="+ Dinheiro recebido" value={formatBRL(totalDinheiro)} />
                <Row label="+ Reforcos" value={formatBRL(totalReforco)} />
                <Row label="- Sangrias" value={formatBRL(totalSangria)} />
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-semibold text-brand-700">
                  <span>Esperado em gaveta</span>
                  <span>{formatBRL(esperadoGaveta)}</span>
                </div>
              </dl>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary" onClick={() => setMovOpen('reforco')}>
              <ArrowUpCircle className="h-4 w-4 text-emerald-600" /> Reforco
            </button>
            <button className="btn-secondary" onClick={() => setMovOpen('sangria')}>
              <ArrowDownCircle className="h-4 w-4 text-red-500" /> Sangria
            </button>
            <button
              className="btn-secondary"
              onClick={() => resumo && setRelatorioResumo(resumo)}
              disabled={!resumo}
            >
              <FileText className="h-4 w-4" /> Relatorio Z (previa)
            </button>
            <button className="btn-secondary" onClick={() => setHistoricoOpen(true)}>
              <History className="h-4 w-4" /> Historico
            </button>
            <button
              className="btn bg-red-600 text-white hover:bg-red-700"
              onClick={() => setFecharOpen(true)}
            >
              <DoorClosed className="h-4 w-4" /> Fechar caixa
            </button>
          </div>

          {(resumo?.movimentos ?? []).length > 0 && (
            <div className="card p-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Movimentacoes</h3>
              <ul className="divide-y divide-slate-100">
                {resumo!.movimentos.map((m) => (
                  <li key={m.id} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <span
                        className={
                          m.tipo === 'reforco'
                            ? 'font-medium text-emerald-700'
                            : 'font-medium text-red-600'
                        }
                      >
                        {m.tipo === 'reforco' ? 'Reforco' : 'Sangria'}
                      </span>
                      {m.motivo && (
                        <span className="ml-2 text-slate-500">• {m.motivo}</span>
                      )}
                      <div className="text-xs text-slate-400">{formatDateTime(m.criado_em)}</div>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {m.tipo === 'reforco' ? '+' : '-'}
                      {formatBRL(Number(m.valor))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <AbrirCaixaModal
        open={abrirOpen}
        onClose={() => setAbrirOpen(false)}
        loading={abrirMut.isPending}
        onSubmit={async (body) => {
          await abrirMut.mutateAsync(body);
          setAbrirOpen(false);
        }}
      />

      <FecharCaixaModal
        open={fecharOpen}
        esperado={esperadoGaveta}
        loading={fecharMut.isPending}
        onClose={() => setFecharOpen(false)}
        onSubmit={async (body) => {
          const caixaFechado = await fecharMut.mutateAsync(body);
          setFecharOpen(false);
          try {
            const resumoFechado = await caixaApi.resumo(caixaFechado.id);
            setRelatorioResumo(resumoFechado);
          } catch {
            // ignora - caixa foi fechado com sucesso mesmo assim
          }
        }}
      />

      <MovimentoModal
        tipo={movOpen}
        loading={movMut.isPending}
        onClose={() => setMovOpen(null)}
        onSubmit={async (body) => {
          await movMut.mutateAsync(body);
          setMovOpen(null);
        }}
      />

      <RelatorioZModal
        open={!!relatorioResumo}
        resumo={relatorioResumo}
        onClose={() => setRelatorioResumo(null)}
      />

      <HistoricoCaixasModal
        open={historicoOpen}
        onClose={() => setHistoricoOpen(false)}
        onSelect={(r) => {
          setHistoricoOpen(false);
          setRelatorioResumo(r);
        }}
      />
    </div>
  );
}

function HistoricoCaixasModal(props: {
  open: boolean;
  onClose: () => void;
  onSelect: (resumo: ResumoCaixa) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['caixa', 'historico'],
    queryFn: () => caixaApi.list(),
    enabled: props.open,
  });
  const [carregandoId, setCarregandoId] = useState<string | null>(null);

  async function handleSelect(c: Caixa) {
    setCarregandoId(c.id);
    try {
      const resumo = await caixaApi.resumo(c.id);
      props.onSelect(resumo);
    } finally {
      setCarregandoId(null);
    }
  }

  return (
    <Modal open={props.open} onClose={props.onClose} title="Historico de caixas" size="lg">
      {isLoading ? (
        <div className="h-40 animate-pulse rounded-lg bg-slate-100" />
      ) : (data ?? []).length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum caixa registrado ainda.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {(data ?? []).map((c) => {
            const divergente = c.valor_divergencia != null && Math.abs(Number(c.valor_divergencia)) > 0.01;
            return (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-slate-900">
                      {c.operador?.nome ?? c.operador_id.slice(0, 8)}
                    </span>
                    <span
                      className={
                        c.status === 'aberto'
                          ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700'
                          : 'rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600'
                      }
                    >
                      {c.status}
                    </span>
                    {divergente && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        divergencia {formatBRL(Number(c.valor_divergencia))}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDateTime(c.abertura_em)}
                    {c.fechamento_em ? ` → ${formatDateTime(c.fechamento_em)}` : ''}
                  </div>
                </div>
                <button
                  className="btn-secondary whitespace-nowrap text-xs"
                  disabled={carregandoId === c.id}
                  onClick={() => void handleSelect(c)}
                >
                  {carregandoId === c.id ? 'Carregando…' : 'Ver relatorio Z'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

function Stat({
  label,
  value,
  small,
  accent,
}: {
  label: string;
  value: string;
  small?: boolean;
  accent?: 'brand' | 'emerald';
}) {
  const accentClass =
    accent === 'brand' ? 'text-brand-700' : accent === 'emerald' ? 'text-emerald-700' : 'text-slate-900';
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 ${small ? 'text-sm' : 'text-xl'} font-semibold ${accentClass}`}>
        {value}
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-slate-700">
      <dt>{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function AbrirCaixaModal(props: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (body: { valor_abertura: number; observacao?: string }) => Promise<void>;
}) {
  const [valor, setValor] = useState('');
  const [obs, setObs] = useState('');

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Abrir caixa"
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={props.onClose} disabled={props.loading}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            disabled={props.loading}
            onClick={() =>
              props.onSubmit({
                valor_abertura: Number(valor) || 0,
                observacao: obs.trim() || undefined,
              })
            }
          >
            {props.loading ? 'Abrindo…' : 'Abrir caixa'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Valor inicial em gaveta (R$)
          </label>
          <input
            type="number"
            min={0}
            step="0.01"
            className="input"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Observacao (opcional)
          </label>
          <textarea
            className="input min-h-[60px]"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            maxLength={500}
          />
        </div>
      </div>
    </Modal>
  );
}

function FecharCaixaModal(props: {
  open: boolean;
  esperado: number;
  loading: boolean;
  onClose: () => void;
  onSubmit: (body: { valor_informado: number; observacao?: string }) => Promise<void>;
}) {
  const [valor, setValor] = useState('');
  const [obs, setObs] = useState('');
  const informado = Number(valor) || 0;
  const divergencia = informado - props.esperado;

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Fechar caixa"
      description="Conte o dinheiro em gaveta e informe o valor conferido."
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={props.onClose} disabled={props.loading}>
            Cancelar
          </button>
          <button
            className="btn bg-red-600 text-white hover:bg-red-700"
            disabled={props.loading || valor === ''}
            onClick={() =>
              props.onSubmit({
                valor_informado: informado,
                observacao: obs.trim() || undefined,
              })
            }
          >
            {props.loading ? 'Fechando…' : 'Fechar caixa'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Esperado em gaveta</span>
            <span className="font-semibold">{formatBRL(props.esperado)}</span>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Valor contado na gaveta (R$)
          </label>
          <input
            type="number"
            min={0}
            step="0.01"
            className="input"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoFocus
          />
          {valor !== '' && (
            <p
              className={`mt-1 text-xs ${
                Math.abs(divergencia) < 0.01
                  ? 'text-emerald-700'
                  : divergencia < 0
                    ? 'text-red-600'
                    : 'text-amber-700'
              }`}
            >
              Divergencia:{' '}
              <strong>
                {divergencia >= 0 ? '+' : ''}
                {formatBRL(divergencia)}
              </strong>
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Observacao (opcional)
          </label>
          <textarea
            className="input min-h-[60px]"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            maxLength={500}
          />
        </div>
      </div>
    </Modal>
  );
}

function MovimentoModal(props: {
  tipo: MovimentoTipo | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: (body: { tipo: MovimentoTipo; valor: number; motivo?: string }) => Promise<void>;
}) {
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const isReforco = props.tipo === 'reforco';

  return (
    <Modal
      open={!!props.tipo}
      onClose={props.onClose}
      title={isReforco ? 'Reforco de caixa' : 'Sangria de caixa'}
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={props.onClose} disabled={props.loading}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            disabled={props.loading || !valor || Number(valor) <= 0}
            onClick={() =>
              props.tipo &&
              props.onSubmit({
                tipo: props.tipo,
                valor: Number(valor),
                motivo: motivo.trim() || undefined,
              })
            }
          >
            {props.loading ? 'Salvando…' : 'Confirmar'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Valor (R$)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            className="input"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Motivo (opcional)
          </label>
          <input
            type="text"
            className="input"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={200}
            placeholder={isReforco ? 'Ex: Troco adicional' : 'Ex: Pagamento fornecedor'}
          />
        </div>
      </div>
    </Modal>
  );
}
